import { useState, useCallback } from "react";
import { sanitizeInput } from "../common/security-utils";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import L from "leaflet";

import locationIcon from "../../assets/svg/location_ic.svg";
import dateIc from "../../assets/svg/date_ic.svg";
import milestoneIcon from "../../assets/svg/flag_ic.svg";
import distanceIcon from "../../assets/png/estimated_km_so.png";
import timestampIcon from "../../assets/svg/time_ic.svg";
import truckIcon from "../../assets/svg/truck_ic.svg";
import redPinIcon from "../../assets/svg/location_pin_red.svg";
import whitePinIcon from "../../assets/svg/location_pin_white.svg";
import productIcon from "../../assets/svg/products_ic.svg";
import arrowDownIcon from "../../assets/svg/down_arrow_blue.svg";
import dollerIc from "../../assets/svg/doller_ic.svg";
import customerIcon from "../../assets/svg/customer_active_ic.svg";

import totalAmtIc from "../../assets/svg/total_amount.svg";
import totalKMIc from "../../assets/svg/total_km.svg";
import totalVolumeIc from "../../assets/svg/total_volume.svg";
import totalPackageIc from "../../assets/svg/total_package.svg";
import totalWeightIc from "../../assets/svg/total_weight.svg";
import stopPinImg from "../../assets/png/planned-pin.png";

const TripDetailsTab = ({
    isLoading,
    viewStopDetails,
    driverList,
    coDriverList,
    deliveryPersonList,
    stopsList,
    productList,
    isProductLoading,
    stopPinLocation,
    polylinePlanned,
    startLocation,
    selectedStopIndex,
    setSelectedStopIndex,
    setMapPosition,
}) => {
    const [selectedContactTab, setSelectedContactTab] = useState("Driver");

    /** Create a numbered stop marker icon */
    const createCustomMarker = (text) =>
        L.divIcon({
            className: "custom-icon",
            html: `
                <div style="position: relative; display: inline-block;">
                    <img src="${stopPinImg}" style="width:40px;height:40px;" />
                    <span style="
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        color: white;
                        font-size: 14px;
                        font-weight: bold;
                    ">
                        ${text}
                    </span>
                </div>
            `,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            popupAnchor: [0, -40],
        });

    /** Create a green START marker icon */
    const createStartMarker = () =>
        L.divIcon({
            className: "custom-icon",
            html: `
                <div style="position: relative; display: inline-block;">
                    <div style="
                        width: 40px;
                        height: 40px;
                        background: #2C8C53;
                        border-radius: 50% 50% 50% 0;
                        transform: rotate(-45deg);
                        border: 3px solid white;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
                    "></div>
                    <span style="
                        position: absolute;
                        top: 50%;
                        left: 50%;
                        transform: translate(-50%, -50%);
                        color: white;
                        font-size: 10px;
                        font-weight: bold;
                    ">START</span>
                </div>
            `,
            iconSize: [40, 40],
            iconAnchor: [20, 40],
            popupAnchor: [0, -40],
        });

    const CONTACT_TABS = [
        { key: "Driver", label: "Driver" },
        { key: "coDriver", label: "Co-Driver" },
        { key: "deliveryPerson", label: "Delivery Person" },
    ];

    const contactListMap = {
        Driver: driverList ?? [],
        coDriver: coDriverList ?? [],
        deliveryPerson: deliveryPersonList ?? [],
    };

    const contactHeaderMap = {
        Driver: "DRIVER",
        coDriver: "CO-DRIVER",
        deliveryPerson: "DELIVER PERSON",
    };

    const SUMMARY_CARDS = [
        { key: "total_amount", icon: totalAmtIc, label: "Total Amount", bg: "#FFFAE5" },
        { key: "total_kilometer", icon: totalKMIc, label: "Total Kilometer", bg: "#EAFFF3" },
        { key: "total_volume", icon: totalVolumeIc, label: "Total Volume", bg: "#F0F1FF" },
        { key: "total_package", icon: totalPackageIc, label: "Total Package", bg: "#FFEBEB" },
        { key: "total_weight", icon: totalWeightIc, label: "Total Weight", bg: "#F7F2FF" },
    ];

    const PRODUCT_COLUMNS = ["PRODUCT NAME", "WEIGHT", "VOLUME", "UNIT", "QUANTITY", "PRICE"];

    if (isLoading) {
        return (
            <div className="row d-flex align-items-stretch">
                <div className="col-12 col-lg-6 mt-2">
                    <div className="bg-white rounded-4 p-3">
                        <div className="skeleton skeleton-text" style={{ width: "150px", height: "18px" }} />
                        {Array.from({ length: 5 }).map((_, idx) => (
                            <div key={idx} className="mt-3 d-flex align-items-center justify-content-between">
                                <div className="skeleton skeleton-text" style={{ width: "180px", height: "14px" }} />
                                <div className="skeleton skeleton-text" style={{ width: "100px", height: "14px" }} />
                            </div>
                        ))}
                    </div>
                </div>
                <div className="col-12 col-lg-6 mt-2">
                    <div className="bg-white rounded-4 p-3">
                        <div className="skeleton skeleton-text" style={{ width: "150px", height: "18px" }} />
                        <div className="mt-2 row">
                            <div className="col-6">
                                <div className="rounded-3 p-3" style={{ background: "#F0F5FF" }}>
                                    <div className="d-flex flex-column align-items-center gap-2">
                                        <div className="skeleton" style={{ width: "64px", height: "64px", borderRadius: "50%" }} />
                                        <div className="skeleton skeleton-text" style={{ width: "100px", height: "18px" }} />
                                        <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                                    </div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="rounded-3 p-2 mb-2" style={{ border: "1px solid #EBEBEB" }}>
                                    <div className="skeleton skeleton-text-sm" style={{ width: "100%", marginBottom: "8px" }} />
                                    <div className="skeleton skeleton-text" style={{ width: "80%", height: "14px" }} />
                                </div>
                                <div className="rounded-3 p-2" style={{ border: "1px solid #EBEBEB" }}>
                                    <div className="skeleton skeleton-text-sm" style={{ width: "60%", marginBottom: "8px" }} />
                                    <div className="skeleton skeleton-text" style={{ width: "80%", height: "14px" }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {/* Trip + Vehicle Details Row */}
            <div className="row d-flex align-items-stretch">
                <div className="col-12 col-lg-6 mt-2">
                    <div className="bg-white rounded-4 p-3 d-flex flex-column align-items-start justify-content-start">
                        <div className="fs-16 fw-600 textFontColor">Trip details</div>

                        <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                                <img src={milestoneIcon} alt="location" />
                                <span className="ms-2 fs-13 fw-500 cardTextGrey">Milestone group</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.milestone ?? "")}</div>
                        </div>

                        <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                                <img src={dateIc} alt="date" />
                                <span className="ms-2 fs-13 fw-500 cardTextGrey">Actual delivery date</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.actdeliverytime ?? "")}</div>
                        </div>

                        <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                                <img src={distanceIcon} alt="date" />
                                <span className="ms-2 fs-13 fw-500 cardTextGrey">Estimated KM</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.est_km ?? "")}</div>
                        </div>

                        <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                                <img src={distanceIcon} alt="date" />
                                <span className="ms-2 fs-13 fw-500 cardTextGrey">Actual KM</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.act_km ?? "")}</div>
                        </div>

                        <div className="mt-3 d-flex align-items-center justify-content-start">
                            <div style={{ width: "200px" }} className="d-flex align-items-center justify-content-start">
                                <img src={timestampIcon} alt="date" />
                                <span className="ms-2 fs-13 fw-500 cardTextGrey">Last tracking on</span>
                            </div>
                            <div className="fs-13 fw-500 textFontColor">{sanitizeInput(viewStopDetails.lasttracking ?? "")}</div>
                        </div>
                    </div>
                </div>

                <div className="h-100 col-12 col-lg-6 mt-2">
                    <div className="h-100 bg-white rounded-4 p-3 d-flex flex-column align-items-start justify-content-start">
                        <div className="fs-16 fw-600 textFontColor">Vehicle details</div>
                        <div className="mt-2 w-100 row d-flex align-items-stretch">
                            <div className="col-6">
                                <div className="h-100 rounded-3 p-3 d-flex flex-column align-items-center justify-content-center" style={{ background: "#F0F5FF" }}>
                                    <div style={{ width: "64px", height: "64px", borderRadius: "100px" }}>
                                        <img src={viewStopDetails.vehicle_profile && viewStopDetails.vehicle_profile.trim() !== "" ? viewStopDetails.vehicle_profile : truckIcon} alt="vehicle" style={{ width: "64px", height: "64px" }} />
                                    </div>
                                    <div className="fs-20 fw-700" style={{ color: "#28303F" }}>
                                        {sanitizeInput(viewStopDetails.license_plate ?? "")}
                                    </div>
                                    <div className="fs-13 fw-500" style={{ color: "#3469E7" }}>
                                        {sanitizeInput(viewStopDetails.vin ?? "")}
                                    </div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div className="rounded-3 px-2 py-3" style={{ border: "1px solid #EBEBEB" }}>
                                    <div className="fs-11 fw-500" style={{ color: "#626F86", textAlign: "start" }}>
                                        VEHICLE IDENTIFICATION NUMBER
                                    </div>
                                    <div className="fs-14 fw-500" style={{ color: "#172B4D", textAlign: "start" }}>
                                        {sanitizeInput(viewStopDetails.vin ?? "")}
                                    </div>
                                </div>

                                <div className="mt-2 rounded-3 px-2 py-3" style={{ border: "1px solid #EBEBEB" }}>
                                    <div className="fs-11 fw-500" style={{ color: "#626F86", textAlign: "start" }}>
                                        VENDOR
                                    </div>
                                    <div className="fs-14 fw-500" style={{ color: "#172B4D", textAlign: "start" }}>
                                        {sanitizeInput(viewStopDetails.vendor ?? "")}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Table */}
            <div className="mt-2 bg-white rounded-4 p-3">
                <div className="row">
                    <div className="col-12 ">
                        <div className="row">
                            <div className="w-100 mb-1  d-flex align-items-center justify-content-start gap-2">
                                {CONTACT_TABS.map((tab) => (
                                    <div
                                        key={tab.key}
                                        className={`cursor-pointer d-flex align-items-center justify-content-center rounded-3  px-2 fs-13  ${selectedContactTab === tab.key ? "fw-500 active-bottom-bar " : " fw-400"} `}
                                        style={{
                                            height: "32px",
                                            minWidth: "110px",
                                            color: selectedContactTab === tab.key ? "#FFFFFF" : "#172B4D",
                                            background: selectedContactTab === tab.key ? "#3469E7" : "#F5F5F5",
                                        }}
                                        onClick={() => setSelectedContactTab(tab.key)}
                                    >
                                        {tab.label}
                                    </div>
                                ))}
                            </div>

                            <>
                                <div style={{ overflowY: "visible" }}>
                                    <div className="mt-2 sticky-table-wrapper ">
                                        <table
                                            className="stop-table"
                                            border="1"
                                            cellPadding="10"
                                            style={{
                                                borderCollapse: "collapse",
                                                width: "100%",
                                                border: "1px solid #EBEBEB",
                                            }}
                                        >
                                            <thead
                                                style={{
                                                    height: "48px",
                                                    color: "#8790A1",
                                                    fontSize: "11px",
                                                    fontWeight: "400",
                                                    textAlign: "left",
                                                }}
                                            >
                                                <tr>
                                                    <th className="ps-2">{contactHeaderMap[selectedContactTab]}</th>
                                                    <th className="ps-2">MOBILE NUMBER</th>
                                                    <th className="ps-2">STARTS ON</th>
                                                    <th className="ps-2">ENDS ON</th>
                                                    <th className="ps-2"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {contactListMap[selectedContactTab].map((row) => (
                                                    <tr
                                                        key={row.id}
                                                        style={{
                                                            height: "60px",
                                                            position: "relative",
                                                            overflow: "visible",
                                                        }}
                                                    >
                                                        <td className="px-2 py-1" style={{ position: "relative", overflow: "visible" }}>
                                                            <div className="pe-3 py-1 fs-14 fw-400 textFontColor" style={{ width: "200px" }}>
                                                                {sanitizeInput(row.driver_name ?? "")}
                                                            </div>
                                                        </td>
                                                        <td className="px-2 py-1 fs-14 fw-400 textFontColor">{sanitizeInput(row.mobile_number ?? "")}</td>
                                                        <td className="px-2 py-1 fs-14 fw-400 textFontColor">{sanitizeInput(row.start_date ?? "")}</td>
                                                        <td className="px-2 py-1 fs-14 fw-400 textFontColor">{sanitizeInput(row.end_date ?? "")}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Metrics */}
            <div className="mt-2 bg-white rounded-4 p-3">
                <div className="row">
                    <div className="col-12 ">
                        <div className="d-flex align-items-center justify-content-between gap-2">
                            {SUMMARY_CARDS.map((card) => (
                                <div key={card.key} className="col-6 col-md-4 col-lg-2 rounded-3 p-2 d-flex align-items-center justify-content-start" style={{ background: card.bg }}>
                                    <img src={card.icon} alt={card.label} />
                                    <div className="ms-2">
                                        <div className="fs-12 fw-400" style={{ color: "#8790A1", textAlign: "start" }}>
                                            {card.label}
                                        </div>
                                        <div className="fs-16 fw-600 mt-1" style={{ color: "#172B4D", textAlign: "start" }}>
                                            {viewStopDetails[card.key] ?? "0"}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Stops Cards */}
                        <div
                            style={{
                                marginTop: "16px",
                                maxHeight: "350px",
                                overflowY: "auto",
                                paddingRight: "8px",
                            }}
                            className="stops-scroll-container"
                        >
                            {stopsList.map((row, index1) => (
                                <div
                                    key={row.stop_id || index1}
                                    className="mt-3"
                                    style={{
                                        border: "1px solid #EBEBEB",
                                        borderRadius: "16px",
                                        backgroundColor: "#FAFAFA",
                                    }}
                                >
                                    {/* Stop ID */}
                                    <div
                                        className="px-2 py-1"
                                        style={{
                                            borderTopLeftRadius: "16px",
                                            borderBottomRightRadius: "16px",
                                            background: "#FFFFFF",
                                            textAlign: "start",
                                            width: "fit-content",
                                            border: "1px solid #EBEBEB",
                                        }}
                                    >
                                        <span className="fs-11 fw-400 textcolor">Stop ID : </span>
                                        <span className="fs-12 fw-500 text-3469E7">#{sanitizeInput(row.stop_code ?? "")}</span>
                                    </div>

                                    {/* Card */}
                                    <div className="d-flex justify-content-between align-items-center p-2">
                                        {/* Left Section */}
                                        <div className="d-flex align-items-center ">
                                            <div
                                                className="d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "80px",
                                                    height: "80px",
                                                    borderRadius: "100px",
                                                    border: "0.5px solid #EBEBEB",
                                                }}
                                            >
                                                <img src={row.profile !== "" ? row.profile : customerIcon} alt="profile" style={{ width: "40px", height: "40px" }} />
                                            </div>

                                            <div className="ms-2 d-flex flex-column align-items-start justify-content-center">
                                                <div className="fs-20 fw-600 textFontColor">{sanitizeInput(row.customer_name ?? "")}</div>
                                                <div className="mt-1 d-flex align-items-center justify-content-start">
                                                    <img src={redPinIcon} alt="location" /> <span className="ms-2 fs-13 fw-300 textFontColor">{sanitizeInput(row.to_address ?? "")}</span>
                                                </div>

                                                <div style={{ fontSize: "14px", marginTop: "5px" }}>
                                                    <span className="fs-13 fw-400 text-A2A7AE">#{sanitizeInput(row.customer_code ?? "")}</span>
                                                    <span
                                                        className="fs-13 fw-500"
                                                        style={{
                                                            marginLeft: "10px",
                                                            backgroundColor: "#D6FEE6",
                                                            color: "#2C8C53",
                                                            padding: "2px 8px",
                                                            borderRadius: "100px",
                                                        }}
                                                    >
                                                        <span className="fs-12 fw-400 text-5F5E5B">Qty : </span> {sanitizeInput(String(row.quantity ?? ""))}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        {/* Right Section */}
                                        <div className="d-flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setSelectedStopIndex(selectedStopIndex === -1 || selectedStopIndex !== index1 ? index1 : -1);
                                                }}
                                                className="btn btn-outline-primary"
                                                style={{
                                                    borderRadius: "100px",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <img src={productIcon} alt="productIcon" /> <span className="px-1 fs-12 fw-500"> Products ({row.product_count ?? ""}) </span>
                                                <img src={arrowDownIcon} alt="arrowDownIcon" />
                                            </button>
                                            {row.to_latitude !== null && row.to_latitude !== "" && row.to_longitude !== null && row.to_longitude !== "" && (
                                                <button
                                                    onClick={() => {
                                                        setMapPosition({
                                                            lat: row.to_latitude,
                                                            lng: row.to_longitude,
                                                        });
                                                    }}
                                                    className="btn btn-primary"
                                                    style={{
                                                        borderRadius: "100px",
                                                        fontWeight: "500",
                                                    }}
                                                >
                                                    <img src={whitePinIcon} alt="location-view" /> <span className=" fs-13 fw-500">View in map</span>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {selectedStopIndex > -1 && selectedStopIndex === index1 && (
                                        <div className="m-2 bg-white rounded-4 p-3">
                                            <div className="row">
                                                <div className="col-12 ">
                                                    <div className="row">
                                                        <div className="col-xl-12 d-flex justify-content-between">
                                                            <p className="fs-18 fw-600 mb-0">Product </p>
                                                            <div className="rounded-4 d-flex justify-content-start p-2" style={{ background: "#FFF7E4" }}>
                                                                <img src={dollerIc} alt="doller" />
                                                                <span className="ms-2 fs-13 fw-400 cardTextGrey">Total :</span> <span className="ms-2 fs-14 fw-500 textFontColor">₹ {productList.reduce((sum, item) => sum + Number(item.price), 0).toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                        <div style={{ overflowY: "visible" }}>
                                                            {isProductLoading ? (
                                                                <div className="mt-2 sticky-table-wrapper ">
                                                                    <table
                                                                        className="stop-table"
                                                                        border="1"
                                                                        cellPadding="10"
                                                                        style={{
                                                                            borderCollapse: "collapse",
                                                                            width: "100%",
                                                                            border: "1px solid #EBEBEB",
                                                                        }}
                                                                    >
                                                                        <thead
                                                                            style={{
                                                                                height: "36px",
                                                                                backgroundColor: "#F8F8F8",
                                                                                color: "#8790A1",
                                                                                fontSize: "11px",
                                                                                fontWeight: "400",
                                                                                textAlign: "left",
                                                                            }}
                                                                        >
                                                                            <tr>
                                                                                {PRODUCT_COLUMNS.map((col) => (
                                                                                    <th key={col} className="ps-2">{col}</th>
                                                                                ))}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {Array.from({ length: 3 }).map((_, idx) => (
                                                                                <tr key={idx} style={{ height: "36px" }}>
                                                                                    {[150, 60, 60, 80, 40, 70].map((w, i) => (
                                                                                        <td key={i} className="px-2 py-1">
                                                                                            <div className="skeleton skeleton-text" style={{ width: `${w}px`, height: "14px" }} />
                                                                                        </td>
                                                                                    ))}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            ) : (
                                                                <div className="mt-2 sticky-table-wrapper ">
                                                                    <table
                                                                        className="stop-table"
                                                                        border="1"
                                                                        cellPadding="10"
                                                                        style={{
                                                                            borderCollapse: "collapse",
                                                                            width: "100%",
                                                                            border: "1px solid #EBEBEB",
                                                                        }}
                                                                    >
                                                                        <thead
                                                                            style={{
                                                                                height: "36px",
                                                                                backgroundColor: "#F8F8F8",
                                                                                color: "#8790A1",
                                                                                fontSize: "11px",
                                                                                fontWeight: "400",
                                                                                textAlign: "left",
                                                                            }}
                                                                        >
                                                                            <tr>
                                                                                {PRODUCT_COLUMNS.map((col) => (
                                                                                    <th key={col} className="ps-2">{col}</th>
                                                                                ))}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {productList?.map((row) => (
                                                                                <tr
                                                                                    key={row.id}
                                                                                    style={{
                                                                                        height: "36px",
                                                                                        position: "relative",
                                                                                        overflow: "visible",
                                                                                    }}
                                                                                >
                                                                                    <td className="px-2 py-1" style={{ position: "relative", overflow: "visible" }}>
                                                                                        <div className="pe-3 py-1">{sanitizeInput(row.product ?? "")}</div>
                                                                                    </td>
                                                                                    <td className="px-2 py-1">{sanitizeInput(String(row.weight ?? ""))}</td>
                                                                                    <td className="px-2 py-1">{sanitizeInput(String(row.voulme ?? ""))}</td>
                                                                                    <td className="px-2 py-1" style={{ width: "150px" }}>{sanitizeInput(row.unit ?? "")}</td>
                                                                                    <td className="px-2 py-1">{sanitizeInput(String(row.quantity ?? ""))}</td>
                                                                                    <td className="px-2 py-1">{sanitizeInput(String(row.price ?? ""))}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Route Map */}
            <div className="mt-2 bg-white rounded-4 p-3">
                <div className="row">
                    <div className="col-12 ">
                        <div className="row">
                            <div className="col-xl-12 d-flex justify-content-start">
                                <p className="fs-18 fw-600 mb-0">Route for this stop </p>
                            </div>
                        </div>

                        <div className="w-100 mt-2">
                            <MapContainer center={[30, 0]} zoom={2} style={{ height: "355px", width: "100%" }}>
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' />

                                <MarkerClusterGroup
                                    iconCreateFunction={(cluster) => {
                                        const markers = cluster.getAllChildMarkers();
                                        return L.divIcon({
                                            html: `
                        <div style="position: relative; display: inline-block;" >
                          <img src="${stopPinImg}"
                                object-fit:cover;" style="
                            width:40px;
                            height:40px;
                        " />
                               <span style="
                                position: absolute;
                                top: 50%;
                                left: 50%;
                                transform: translate(-50%, -50%);
                                color: white;
                                font-size: 13px;
                                font-weight: bold;
                              ">
                               ... ${markers.length}
                              </span>
                        </div>
                      `,
                                            className: "custom-cluster",
                                            iconSize: L.point(50, 50),
                                        });
                                    }}
                                >
                                    {startLocation && (
                                        <Marker key="start-location" position={[startLocation.lat, startLocation.lng]} icon={createStartMarker()}>
                                            <Popup>
                                                <div style={{ minWidth: "200px", padding: "10px" }}>
                                                    <div className="fw-600 fs-14 mb-2">TRIP START</div>
                                                    <div className="fs-12 mb-1">
                                                        <strong>Starting Point</strong>
                                                    </div>
                                                    <div className="fs-12">This is where the trip begins</div>
                                                </div>
                                            </Popup>
                                        </Marker>
                                    )}

                                    {stopPinLocation.map((coord, index) => {
                                        const markerIcon = createCustomMarker(coord.sequence || index + 1);

                                        return (
                                            <Marker
                                                key={`Stop${index}`}
                                                position={[coord.to_latitude, coord.to_longitude]}
                                                icon={markerIcon}
                                                eventHandlers={{
                                                    mouseover: (e) => {
                                                        e.target.openPopup();
                                                    },
                                                    mouseout: (e) => {
                                                        e.target.closePopup();
                                                    },
                                                }}
                                            >
                                                <Popup>
                                                    <div style={{ minWidth: "250px", padding: "10px" }}>
                                                        <div className="fw-600 fs-14 mb-2">Stop #{coord.sequence || index + 1}</div>
                                                        {coord.customer_name && (
                                                            <div className="fs-12 mb-1">
                                                                <strong>Customer:</strong> {sanitizeInput(coord.customer_name)}
                                                            </div>
                                                        )}
                                                        {coord.customer_code && (
                                                            <div className="fs-12 mb-1">
                                                                <strong>Code:</strong> {sanitizeInput(coord.customer_code)}
                                                            </div>
                                                        )}
                                                        <div className="fs-12 mb-1">
                                                            <strong>Stop Code:</strong> {sanitizeInput(coord.stop_code ?? "")}
                                                        </div>
                                                        <div className="fs-12 mb-1">
                                                            <strong>Address:</strong> {sanitizeInput(coord.to_address ?? "")}
                                                        </div>
                                                        {coord.product_count > 0 && (
                                                            <div className="fs-12 mb-1">
                                                                <strong>Products:</strong> {coord.product_count}
                                                            </div>
                                                        )}
                                                        {coord.quantity && (
                                                            <div className="fs-12 mb-1">
                                                                <strong>Quantity:</strong> {sanitizeInput(String(coord.quantity))}
                                                            </div>
                                                        )}
                                                        <div className="fs-12">
                                                            <strong>Status:</strong>{" "}
                                                            <span className="badge" style={{ background: "#EDF2FF", color: "#3469E7" }}>
                                                                {sanitizeInput(coord.status ?? "")}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </Popup>
                                            </Marker>
                                        );
                                    })}
                                </MarkerClusterGroup>
                                {polylinePlanned.map((poly) => (
                                    <Polyline key={`${poly.id}`} positions={poly.positionList} color="#FDCA01" weight={5} />
                                ))}
                            </MapContainer>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TripDetailsTab;
